import { usePageMeta } from "@/components/pepmuse/usePageMeta";
import { LegalLayout, LegalSection } from "@/components/pepmuse/LegalLayout";

export default function Terms() {
  usePageMeta("Terms of Use — PepMuse", "The terms governing use of the PepMuse website and services.");

  return (
    <LegalLayout testId="terms-page" eyebrow="Legal" title="Terms of Use" updated="September 6, 2026">
      <LegalSection title="The agreement">
        <p>
          These Terms of Use govern your use of the PepMuse website and any services you engage us for. By using this
          site — including the launch checklist and inquiry form — you agree to these terms. If you don't agree,
          please don't use the site.
        </p>
      </LegalSection>

      <LegalSection title="What PepMuse does">
        <p>
          PepMuse provides branding, website creation, and launch support services for founders building research
          peptide brands. PepMuse is not a peptide manufacturer, supplier, distributor, or retailer, and nothing on
          this site is an offer to sell peptides or any other regulated product.
        </p>
      </LegalSection>

      <LegalSection title="Research use only">
        <p>
          The businesses we serve operate in the research peptide space. Products offered by brands we work with are
          intended strictly for laboratory research use only — not for human or veterinary consumption, and not to
          diagnose, treat, cure, or prevent any disease.
        </p>
        <p>
          Nothing on this site is medical advice, and we never make health or therapeutic claims. Our compliance
          support — frameworks, disclaimers, label templates — is practical help, not legal advice, and it doesn't
          replace a qualified attorney or regulatory professional.
        </p>
        <p>
          We may decline or discontinue work that, in our judgment, markets products for human consumption or implies
          unapproved therapeutic use.
        </p>
      </LegalSection>

      <LegalSection title="Estimates, quotes, and payment">
        <p>
          The launch checklist generates ballpark estimates from our standard service pricing. An estimate or proposal
          number is not a binding quote, an invoice, or a guarantee of availability or timeline — final scope, price,
          and schedule are confirmed together in writing before any work begins or payment is due.
        </p>
      </LegalSection>

      <LegalSection title="Your responsibilities">
        <p>If you engage our services, you're responsible for:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>The lawful formation and operation of your business in your jurisdiction</li>
          <li>The accuracy of the information you give us</li>
          <li>Final review and approval of the content, labels, and claims published under your brand</li>
          <li>Your own legal, tax, and regulatory compliance — including research-use-only requirements</li>
        </ul>
      </LegalSection>

      <LegalSection title="Intellectual property">
        <p>
          Once a project is paid in full, the final deliverables we created for you are yours. Until then, they remain
          ours. We may show completed work in our portfolio unless we agree otherwise in writing. The PepMuse name,
          logo, and this site's content belong to us and may not be reused without permission.
        </p>
      </LegalSection>

      <LegalSection title="No professional advice">
        <p>
          Content on this site — including the checklist — is general information, not legal, medical, financial, or
          regulatory advice. Decisions about your business should be made with qualified professionals.
        </p>
      </LegalSection>

      <LegalSection title="Disclaimers">
        <p>
          This site and its tools are provided "as is" and "as available". We make no warranties, express or implied,
          about uninterrupted operation, accuracy of estimates, or fitness for a particular purpose.
        </p>
      </LegalSection>

      <LegalSection title="Limitation of liability">
        <p>
          To the fullest extent permitted by law, PepMuse is not liable for indirect, incidental, or consequential
          damages — lost profits, lost revenue, lost data, or business interruption — arising from your use of this
          site or reliance on its tools. Our total liability for any claim related to the site is limited to the
          amount you paid us, if any, for services in the twelve months before the claim.
        </p>
      </LegalSection>

      <LegalSection title="Indemnification">
        <p>
          You agree to indemnify and hold PepMuse harmless from claims arising out of your business operations, your
          products, your content, or your breach of these terms.
        </p>
      </LegalSection>

      <LegalSection title="Acceptable use">
        <p>
          Don't misuse this site: no scraping at scale, no interfering with its operation, no unlawful content through
          our forms, and no misrepresenting who you are.
        </p>
      </LegalSection>

      <LegalSection title="Governing law, changes, and contact">
        <p>
          These terms are governed by the laws of the State of Delaware, USA, without regard to conflict-of-law rules.
          We may update these terms from time to time; the version posted here with its "last updated" date controls.
          Questions? Reach us through the project inquiry form on this site.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
